import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { type JSX, useEffect, useState } from 'react';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { fetchBillingAccountsCoco } from '../../../../common/fetch.js';
import {
  billingAccountNameMsg,
  eInvoiceMsg,
  emailInvoiceMsg,
  newBillingAgreementMsg,
  paperInvoiceMsg,
  selectMsg,
  t,
} from '../../../../common/i18n/index.js';
import { formatAddress } from '../../../../common/utils/accountUtils.js';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../../../common/utils/billingAccountUtils.js';
import {
  type BillingAccountType,
  getBillingAccountBillReceiverName,
  getBillingAccountDeliveryMethod,
  getBillingAccountElectronicDetails,
  getBillingAccountPayerAddress,
  getBillingAccountPayerName,
} from '../../../../common/utils/billingAccountUtilsCoCo.js';
import { BillingAccountDeliveryMethod } from '../../../../generated/api/models.js';
import { BillingAccountFormCoCo } from '../../../BillingAccountForm/BillingAccountFormCoCo.js';
import { toContactOptions } from '../../../BillingAccountForm/billingAccountCocoFormModel.js';
import { useCreateBillingAccountCoco } from '../../../BillingAccountForm/useCreateBillingAccountCoco.js';
import type { Address } from '../../../../generated/api/address.js';
import type { BillChannel } from '../../../../generated/api/billChannel.js';
import type { BillingAccountCoco } from '../../../../generated/api/billingAccountCoco.js';
import type { CustomerResponse } from '../../../../generated/api/customerResponse.js';
import type { RelatedPartyRef } from '../../../../generated/api/relatedPartyRef.js';

export interface BillingAccountCocoCreationConfig {
  billingAccountType: BillingAccountType;
  billChannels: BillChannel[];
  relatedParties: RelatedPartyRef[];
  customer?: CustomerResponse;
}

interface SelectBillingAccountCoCoProps {
  onChange: (billingAccount: BillingAccountCoco) => void;
  onCreatingNewBillingAccountChange?: (isCreating: boolean) => void;
  billingAccountId?: string;
  infoText?: JSX.Element;
  mdmId: string;
  // Already loaded billing accounts of the company, they are fetched by the component when not given
  billingAccounts?: BillingAccountCoco[];
  error?: string;
  disabled?: boolean;
  createBillingAccount?: BillingAccountCocoCreationConfig;
  onBillingAccountCreated?: (billingAccount: BillingAccountCoco) => void;
  onBillingAccountError?: (message: string) => void;
}

const InvoiceDeliveryMethodCoCo = ({
  deliveryMethod,
  billReceiverName,
  payerAddress,
  billElectronicAddress,
  billElectronicOperator,
}: {
  deliveryMethod: BillingAccountDeliveryMethod;
  billReceiverName?: string;
  payerAddress: Address;
  billElectronicOperator?: string;
  billElectronicAddress?: string;
}) => {
  switch (deliveryMethod) {
    case BillingAccountDeliveryMethod.EMAIL:
      return (
        <>
          <span>{billReceiverName}</span>
          <br />
          <span>{t.NNV9(emailInvoiceMsg)}</span>
        </>
      );
    case BillingAccountDeliveryMethod.PAPER:
      return (
        <>
          <span>{formatAddress(payerAddress)}</span>
          <br />
          <span>{t.TER4(paperInvoiceMsg)}</span>
        </>
      );
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return (
        <>
          <span>{billElectronicOperator + ' ' + billElectronicAddress}</span>
          <br />
          <span key="deliveryMethod">{t.IK1D(eInvoiceMsg)}</span>
        </>
      );
    default:
      return null;
  }
};

export const BillingAccountInfoCoCo = ({ billingAccount }: { billingAccount: BillingAccountCoco }) => (
  <div className="of-order-delivery-options__billing-account-info" aria-label={t.AMRD('Billing account details')}>
    <span key="payerName">{getBillingAccountPayerName(billingAccount)}</span>
    <br />
    <InvoiceDeliveryMethodCoCo
      deliveryMethod={getBillingAccountDeliveryMethod(billingAccount)}
      payerAddress={getBillingAccountPayerAddress(billingAccount)}
      billElectronicAddress={getBillingAccountElectronicDetails(billingAccount).address}
      billElectronicOperator={getBillingAccountElectronicDetails(billingAccount).operator}
      billReceiverName={getBillingAccountBillReceiverName(billingAccount)}
    />
  </div>
);

const renderBillingAccountOption = (ba: BillingAccountCoco, label: string) => (
  <div className="of-billing-account-search-result" key={ba.id}>
    <h4 className="ea-h4">{label}</h4>
    {ba.name && (
      <div>
        {t.RH6T(billingAccountNameMsg)}: {ba.name}
      </div>
    )}
  </div>
);

const createNewBillingAccountOption = {
  id: CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE,
  label: t.NCIJ(newBillingAgreementMsg),
  value: CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE,
  html: (
    <div className="of-billing-account-search-result">
      <span>{t.NCIJ(newBillingAgreementMsg)}</span>
    </div>
  ),
};

const CreateBillingAccountCoCo = ({
  config,
  mdmId,
  onCreated,
  onCancel,
  onError,
}: {
  config: BillingAccountCocoCreationConfig;
  mdmId: string;
  onCreated: (billingAccount: BillingAccountCoco) => void;
  onCancel: () => void;
  onError?: (message: string) => void;
}) => {
  const { createBillingAccount, isSaving } = useCreateBillingAccountCoco({
    billingAccountType: config.billingAccountType,
    relatedParties: config.relatedParties,
    mdmId,
    onError,
  });
  const contacts = toContactOptions(config.customer?.relatedParty);

  const handleSubmit = async (values: Parameters<typeof createBillingAccount>[0]): Promise<void> => {
    const created = await createBillingAccount(values);
    if (created) {
      onCreated(created);
    }
  };

  return (
    <BillingAccountFormCoCo
      billChannels={config.billChannels}
      contacts={contacts}
      customer={config.customer}
      isSaving={isSaving}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
};

export const SelectBillingAccountCoCo = ({
  onChange,
  onCreatingNewBillingAccountChange,
  billingAccountId,
  infoText,
  mdmId,
  billingAccounts: loadedBillingAccounts,
  error,
  disabled = false,
  createBillingAccount,
  onBillingAccountCreated,
  onBillingAccountError,
}: SelectBillingAccountCoCoProps) => {
  const [selectedBillingAccount, setSelectedBillingAccount] = useState<BillingAccountCoco | null>(null);
  const [fetchedBillingAccounts, setFetchedBillingAccounts] = useState<BillingAccountCoco[]>([]);
  const [createdBillingAccounts, setCreatedBillingAccounts] = useState<BillingAccountCoco[]>([]);
  const availableBillingAccounts = loadedBillingAccounts ?? fetchedBillingAccounts;
  const billingAccounts = [
    ...availableBillingAccounts,
    ...createdBillingAccounts.filter(created => !availableBillingAccounts.some(({ id }) => id === created.id)),
  ];
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);

  useEffect(() => {
    onCreatingNewBillingAccountChange?.(isCreatingNewBillingAccount);
  }, [isCreatingNewBillingAccount, onCreatingNewBillingAccountChange]);

  const formattedBillingAccounts = billingAccounts.map(ba => {
    const label = [ba.abpAccountNumber, getBillingAccountPayerName(ba), ba.name].filter(Boolean).join(' ');

    return {
      id: ba.id ?? '',
      label,
      value: ba.id ?? '',
      html: renderBillingAccountOption(ba, label),
    };
  });
  if (createBillingAccount && !disabled) {
    formattedBillingAccounts.unshift(createNewBillingAccountOption);
  }

  const selectBillingAccount = (billingAccount: BillingAccountCoco) => {
    setSelectedBillingAccount(billingAccount);
    onChange(billingAccount);
  };

  const refreshBillingAccounts = async (): Promise<BillingAccountCoco[]> => {
    try {
      const accounts = await fetchBillingAccountsCoco(undefined, { mdmId });
      setFetchedBillingAccounts(accounts);
      return accounts;
    } catch {
      setFetchedBillingAccounts([]);
      return [];
    }
  };

  const handleCreated = (createdAccount: BillingAccountCoco) => {
    setIsCreatingNewBillingAccount(false);
    onCreatingNewBillingAccountChange?.(false);
    setCreatedBillingAccounts(accounts => [...accounts, createdAccount]);
    selectBillingAccount(createdAccount);
    onBillingAccountCreated?.(createdAccount);
  };

  const startCreatingNewBillingAccount = () => {
    setIsCreatingNewBillingAccount(true);
    setSelectedBillingAccount(null);
  };

  const onValueSelect = (value?: string, element?: HTMLElement) => {
    const selectedValue = element?.dataset?.value ?? value;

    if (selectedValue === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && createBillingAccount) {
      startCreatingNewBillingAccount();
      return;
    }
    if (selectedValue) {
      const billingAccount = billingAccounts.find(account => account.id === selectedValue);
      if (billingAccount) {
        selectBillingAccount(billingAccount);
      }
    }
  };

  useEffect(() => {
    if (loadedBillingAccounts) {
      return;
    }
    void refreshBillingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdmId]);

  useEffect(() => {
    if (!billingAccountId || selectedBillingAccount?.id === billingAccountId) {
      return;
    }
    const exists = billingAccounts.some(ba => ba.id === billingAccountId);
    if (exists) {
      const billingAccount = billingAccounts.find(account => account.id === billingAccountId);
      if (billingAccount) {
        selectBillingAccount(billingAccount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAccountId, billingAccounts, selectedBillingAccount, mdmId]);

  return (
    <>
      <CL.Combobox
        i18n_combobox_errorMessage={error}
        label={t.PUM6('Choose billing account')}
        i18n_combobox_infoText={infoText}
        onValueSelect={onValueSelect}
        items={formattedBillingAccounts}
        selectedValue={
          isCreatingNewBillingAccount
            ? CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE
            : (selectedBillingAccount?.id ?? billingAccountId ?? '')
        }
        selectedItemFormat="label"
        onValueChange={value => {
          if (value === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && createBillingAccount) {
            startCreatingNewBillingAccount();
          }
        }}
        i18n_combobox_buttonAriaLabel={t.HVS2('Selected billing account')}
        i18n_combobox_placeholderText={t.QRYV(selectMsg)}
        disabled={disabled}
      />

      {isCreatingNewBillingAccount && createBillingAccount && (
        <CreateBillingAccountCoCo
          config={createBillingAccount}
          mdmId={mdmId}
          onCreated={handleCreated}
          onCancel={() => {
            setIsCreatingNewBillingAccount(false);
            onCreatingNewBillingAccountChange?.(false);
          }}
          onError={onBillingAccountError}
        />
      )}

      {selectedBillingAccount ? (
        <div className={classNames(dsClass.MARGIN_VERTICAL_4)}>
          <BillingAccountInfoCoCo billingAccount={selectedBillingAccount} />
        </div>
      ) : null}
    </>
  );
};
